import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  axisProps,
  CHART_MARGIN,
  formatCompactKr,
  formatWholeKr,
  gridProps,
  tooltipContentStyle,
  tooltipLabelStyle,
  useIsSmUp,
} from '../insights/chart';
import { periodLabel, periodShortLabel, tickInterval, toKr } from './cost-history';

export type CostChartPoint = {
  year: number;
  month: number;
  /** Öre; ignored when paymentCount is 0 */
  amount: number;
  paymentCount: number;
};

type Datum = {
  key: string;
  year: number;
  label: string;
  shortLabel: string;
  /** Whole kronor, null when nothing was paid (an empty slot, not a zero bar) */
  value: number | null;
  paymentCount: number;
  isCurrent: boolean;
};

/** 0 and three or four round steps above `max`, e.g. 0, 500, 1 000, 1 500 */
function niceTicks(max: number): number[] {
  if (max <= 0) return [0, 100];
  const raw = max / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? raw;
  const top = Math.ceil(max / step) * step;
  return Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
}

/** Like formatCompactKr but keeps one decimal under 10k, so 1 500 reads "1,5k" not "2k" */
export function formatAxisKr(kr: number) {
  if (Math.abs(kr) >= 10_000 || Math.abs(kr) < 1000) return formatCompactKr(kr);
  return `${(kr / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })}k`;
}

const CURRENT_FILL = 'var(--primary)';
const PAST_FILL = 'var(--chart-2)';

/**
 * Bars per budget period with the ongoing period highlighted and a dashed
 * average line. Periods without payments stay empty.
 */
export function CostHistoryChart({
  points,
  average,
  height,
  ariaLabel,
}: {
  points: CostChartPoint[];
  /** Öre, drawn as a reference line */
  average: number | null;
  height: { base: number; sm: number };
  ariaLabel: string;
}) {
  const isSmUp = useIsSmUp();
  const lastIndex = points.length - 1;
  const data: Datum[] = points.map((p, index) => ({
    key: `${p.year}-${p.month}`,
    year: p.year,
    label: periodLabel(p.year, p.month),
    shortLabel: periodShortLabel(p.month),
    value: p.paymentCount > 0 ? toKr(p.amount) : null,
    paymentCount: p.paymentCount,
    isCurrent: index === lastIndex,
  }));
  const maxKr = Math.max(0, ...data.map((d) => d.value ?? 0), average ? toKr(average) : 0);
  const yTicks = niceTicks(maxKr);
  const byKey = new Map(data.map((d) => [d.key, d]));
  // Thin the ticks from the newest period backwards so the current one is always labelled
  const step = tickInterval(data.length, isSmUp) + 1;
  const ticks = data.filter((_, index) => (lastIndex - index) % step === 0).map((d) => d.key);
  // Ranges over a year repeat month names: tag the first tick of each new year
  const yearMarks = new Set(
    data.length > 12
      ? ticks.filter((key, i) => byKey.get(key)?.year !== byKey.get(ticks[i - 1] ?? '')?.year)
      : [],
  );

  return (
    <div className="space-y-2">
      <div role="img" aria-label={ariaLabel}>
        <ResponsiveContainer width="100%" height={isSmUp ? height.sm : height.base}>
          <BarChart data={data} margin={CHART_MARGIN} barCategoryGap="18%">
            <CartesianGrid {...gridProps} vertical={false} />
            <XAxis
              {...axisProps}
              dataKey="key"
              ticks={ticks}
              interval={0}
              tickFormatter={(key: string) => {
                const d = byKey.get(key);
                if (!d) return '';
                if (isSmUp && data.length <= 6) return d.label;
                return yearMarks.has(key)
                  ? `${d.shortLabel} ’${String(d.year).slice(2)}`
                  : d.shortLabel;
              }}
            />
            <YAxis
              {...axisProps}
              width={isSmUp ? 48 : 36}
              domain={[0, yTicks.at(-1) ?? 'auto']}
              ticks={yTicks}
              tickFormatter={formatAxisKr}
            />
            <Tooltip
              cursor={{ fill: 'var(--muted)', opacity: 0.6 }}
              content={({ active, payload }) => {
                const d = payload?.[0]?.payload as Datum | undefined;
                if (!active || !d) return null;
                return (
                  <div style={{ ...tooltipContentStyle, padding: '8px 10px' }}>
                    <div style={tooltipLabelStyle}>
                      {d.label}
                      {d.isCurrent ? ' · pågår' : ''}
                    </div>
                    <div className="tabular-nums font-medium">
                      {d.value == null ? 'Ingen betalning' : formatWholeKr(d.value)}
                    </div>
                    {d.paymentCount > 1 && (
                      <div style={tooltipLabelStyle}>{d.paymentCount} betalningar</div>
                    )}
                  </div>
                );
              }}
            />
            {average != null && average > 0 && (
              <ReferenceLine
                y={toKr(average)}
                stroke="var(--muted-foreground)"
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
              />
            )}
            <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={32} isAnimationActive={false}>
              {data.map((d) => (
                <Cell key={d.key} fill={d.isCurrent ? CURRENT_FILL : PAST_FILL} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className="size-2.5 rounded-sm"
            style={{ backgroundColor: CURRENT_FILL }}
          />
          Pågående period
        </span>
        {average != null && average > 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="w-3 border-t-2 border-dashed border-muted-foreground" />
            Snitt {formatWholeKr(toKr(average))}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Tiny bar sparkline for a bill card. Unpaid periods get a faint baseline
 * tick so a gap reads as "no payment" rather than a zero cost.
 */
export function CostSparkline({ points }: { points: CostChartPoint[] }) {
  const lastIndex = points.length - 1;
  const max = Math.max(0, ...points.filter((p) => p.paymentCount > 0).map((p) => p.amount));
  const marker = max > 0 ? max * 0.06 : 1;
  const data = points.map((p, index) => ({
    key: `${p.year}-${p.month}`,
    paid: p.paymentCount > 0 ? Math.max(p.amount, 0) : null,
    empty: p.paymentCount > 0 ? null : marker,
    isCurrent: index === lastIndex,
  }));

  return (
    <div aria-hidden className="pointer-events-none">
      <ResponsiveContainer width="100%" height={36}>
        <BarChart
          data={data}
          margin={{ top: 2, right: 0, bottom: 0, left: 0 }}
          barCategoryGap="14%"
        >
          <YAxis hide domain={[0, max > 0 ? max : 1]} />
          <Bar dataKey="paid" stackId="s" radius={[3, 3, 0, 0]} isAnimationActive={false}>
            {data.map((d) => (
              <Cell key={d.key} fill={d.isCurrent ? CURRENT_FILL : PAST_FILL} />
            ))}
          </Bar>
          <Bar
            dataKey="empty"
            stackId="s"
            fill="var(--border)"
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
